/**
 * Analytics v1 at a turn's two client moments: its first token reaching the
 * client, and its finished answer reaching the screen. The handler glue calls
 * these; they live here so streamingHandlers (a ratcheted file) only calls.
 */

import {
  ANALYTICS_EVENTS,
  terminalReasonFor,
  type TerminalReasonView,
  type TurnOutcome,
} from "@bash/shared";
import { captureV1, clientSurface } from "@/lib/analytics-v1";
import { isPageVisible } from "@/lib/page-visibility";
import { contextOf } from "@/lib/composer/screen-context";
import type { PerceivedFirstTokenEvent } from "./perceivedTtft";
import type { TaskState } from "../types";

/**
 * task:first_token. Only a typed send arms the clock (the three classic send
 * forks), so the input mode is typed; the task carries its engine and model.
 */
export function captureFirstToken(
  taken: PerceivedFirstTokenEvent,
  task: Pick<TaskState, "runtime" | "model">
): void {
  captureV1(ANALYTICS_EVENTS.TASK_FIRST_TOKEN, {
    ...taken,
    surface: clientSurface(),
    runtime: task.runtime,
    model: task.model ?? null,
    input_mode: "typed",
  });
}

/**
 * Did the turn end with a whole answer? Only terminal status 'ok' is a result:
 * a stopped, truncated, paused or failed turn is not one. The runner's own
 * terminal reason wins; otherwise it is derived from the outcome by the same
 * contract function. An end that carries neither is not known to be a result.
 */
export function turnEndedOk(data: {
  outcome?: TurnOutcome;
  terminalReason?: TerminalReasonView;
}): boolean {
  const status =
    data.terminalReason?.status ??
    (data.outcome ? terminalReasonFor(data.outcome).status : undefined);
  return status === "ok";
}

/**
 * delegation:result_presented: a finished turn's answer landed on the screen
 * the person is looking at, the task open on a visible task route. `isResult`
 * is true only for a turn that ended 'ok' (turnEndedOk) and is not waiting on
 * the person. An answer that lands while they are elsewhere was not presented.
 */
export function noteResultPresented(
  taskId: string,
  isResult: boolean,
  selectedTaskId: string | null,
  messageId?: string
): void {
  if (!isResult || typeof window === "undefined" || !isPageVisible()) return;
  const onScreen = contextOf(window.location.pathname, selectedTaskId);
  if (onScreen.activeTaskId !== taskId) return;
  captureV1(ANALYTICS_EVENTS.DELEGATION_RESULT_PRESENTED, {
    task_id: taskId,
    ...(messageId ? { message_id: messageId } : {}),
  });
}
